<template>
  <div>
    <div v-if="!showConfirmationSlot" :class="$style.container">
      <!-- ******************* -->
      <!-- ERROR/SUCCESS ALERT -->
      <!-- ******************* -->
      <div
        v-if="!!(serverErrorMessage || paymentSuccessful)"
        :class="$style.alertWrapper"
      >
        <NsAlert
          :class="$style.alert"
          type="card"
          :context="paymentSuccessful ? 'success' : 'error'"
          :title="paymentSuccessful ? successLabel : errorLabel"
          visible
          @dismiss="handleDismissAlert"
        >
          <template #body>
            {{ paymentSuccessful ? successMessage : serverErrorMessage }}
          </template>
        </NsAlert>
      </div>
      <!-- ************* -->
      <!-- DONATION FORM -->
      <!-- ************* -->
      <form data-testid="donation-form" :class="$style.form" novalidate>
        <!-- LEFT SECTION -->
        <!-- ************ -->
        <div :class="$style.formSection">
          <h4 :class="$style.sectionTitle">
            {{ personalInfoTitle }}
          </h4>
          <NsTextField
            id="first-name"
            v-model="firstName"
            :class="['text-field', { error: errors.firstName }]"
            :label="firstNameLabel"
            type="text"
            required
            @blur="checkValidation('firstName')"
            @focus="removeErrors('firstName')"
          >
            {{ errors.firstName ? fieldRequiredText : "" }}
          </NsTextField>
          <NsTextField
            id="last-name"
            v-model="lastName"
            :class="['text-field', { error: errors.lastName }]"
            :label="lastNameLabel"
            type="text"
            required
            @blur="checkValidation('lastName')"
            @focus="removeErrors('lastName')"
          >
            {{ errors.lastName ? fieldRequiredText : "" }}
          </NsTextField>
          <NsTextField
            id="email"
            v-model="email"
            :class="['text-field', { error: errors.email }]"
            :label="emailLabel"
            type="text"
            required
            @blur="checkValidation('email')"
            @focus="removeErrors('email')"
          >
            {{
              errors.email
                ? !email
                  ? fieldRequiredText
                  : invalidEmailText
                : ""
            }}
          </NsTextField>
          <NsTextField
            id="affiliate-id"
            v-model="distId"
            :class="'text-field'"
            :label="`${nuskinIdLabel} (${optionalLabel})`"
            type="text"
          />
        </div>
        <div :class="$style.separator" />
        <!-- RIGHT SECTION -->
        <!-- ************* -->
        <div :class="$style.formSection">
          <!-- AMOUNT SECTION -->
          <h4 :class="$style.sectionTitle">
            {{ amountTitle }}
          </h4>
          <div :class="$style.donationAmounts" :style="donationBtnsStyle">
            <button
              v-for="amt in buttonAmounts"
              :key="amt.value"
              type="button"
              :class="[
                'btn-primary',
                amt.value === buttonAmount ? 'hit-solid' : 'primary-outline',
                $style.donationAmount
              ]"
              @click="updateAmount(amt.value)"
            >
              {{ amt.displayText }}
            </button>
          </div>
          <NsCurrencyTextField
            id="custom-amount"
            v-model="customAmount"
            :class="['text-field', { error: errors.donationAmount }]"
            icon="icon-dollar"
            :label="amountLabel"
            type="text"
            required
            @blur="checkValidation('donationAmount')"
            @focus="focusCustomAmount"
          >
            {{ +errors.donationAmount ? minDonationAmountText : "" }}
          </NsCurrencyTextField>
          <div :class="$style.sectionSpacer" />

          <!-- DONATION TYPE SECTION -->
          <template v-if="showRecurringOption">
            <h4 :class="$style.sectionTitle">{{ donationTypeLabel }}</h4>
            <div :class="$style.donationTypes">
              <label :class="$style.typeLabel">
                <input
                  v-model="donationType"
                  type="radio"
                  name="donationType"
                  value="oneTime"
                />
                {{ oneTimeLabel }}
              </label>
              <label :class="$style.typeLabel">
                <input
                  v-model="donationType"
                  type="radio"
                  name="donationType"
                  value="monthly"
                />
                {{ monthlyLabel }}
              </label>
              <div
                v-show="donationType === 'monthly' && monthlyTermsMessage"
                :class="$style.donationText"
              >
                {{ monthlyTermsMessage }}
              </div>
            </div>
          </template>

          <!-- CREDIT CARD SECTION -->
          <h4 :class="$style.sectionTitle">{{ cardTitle }}</h4>
          <NsCreditCard
            ref="creditCardNumber"
            v-model="cardNumber"
            :available-payments="availablePayments"
            :translations="{
              fieldLabel: cardNumberLabel,
              fieldRequired: fieldRequiredText,
              notRecognizedCard: invalidCardNumberText,
              placeholder: '    -    -    -    '
            }"
          />
          <div :class="$style.expiryCvcRow">
            <div :class="$style.rowItem">
              <NsCreditCardDate
                ref="creditCardDate"
                v-model="cardExpiry"
                :translations="{
                  label: expiryLabel,
                  monthPlaceholder: monthPlaceholderText,
                  yearPlaceholder: yearPlaceholderText,
                  fieldRequired: fieldRequiredText
                }"
                @date-valid-state="validDate = $event"
              />
            </div>
            <div :class="$style.rowItem">
              <NsTextField
                id="card-cvc"
                v-model="cardCvc"
                :class="['text-field', { error: errors.cardCvc }]"
                :label="cvcLabel"
                type="text"
                required
                @blur="checkValidation('cardCvc')"
                @focus="removeErrors('cardCvc')"
              >
                {{ errors.cardCvc ? fieldRequiredText : "" }}
              </NsTextField>
            </div>
          </div>
          <button
            type="button"
            class="hit-solid fluid"
            :class="{ [$style.buttonProcessing]: processing }"
            :diabled="processing"
            @click="pay"
          >
            <span v-if="!processing"
              >{{ submitLabel }}{{ formattedDonationAmount }}</span
            >
            <span v-if="processing" :class="$style.processing"
              >{{ processingText }}<span>.</span><span>.</span
              ><span>.</span></span
            >
          </button>
        </div>
      </form>
    </div>

    <!-- ***************** -->
    <!-- CONFIRMATION SLOT -->
    <!-- ***************** -->
    <slot
      v-if="showConfirmationSlot"
      :transaction="transaction"
      :handleBack="backToDonationForm"
    />
  </div>
</template>

<script>
import {
  NsTextField,
  NsCreditCard,
  NsCreditCardDate,
  NsAlert
} from "@nuskin/design-components";
import { fromJsonString } from "@nuskin/ns-common-lib";
import NsCurrencyTextField from "./NsCurrencyTextField.vue";
import axios from "axios";
const emailPattern = /^[\s]*[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?[\s]*$/;
// availablePayments: Visa, MC, Disc, AMEX
const availablePayments = [{ id: 0 }, { id: 1 }, { id: 2 }, { id: 3 }];

const QB_TOKEN_URI = "https://api.intuit.com/quickbooks/v4/payments/tokens";
const QB_TOKEN_URI_SANDBOX =
  "https://sandbox.api.intuit.com/quickbooks/v4/payments/tokens";
const QB_CHARGE_CARD_URI = "https://api.cloud.nuskin.com/ffg-qb/v1/pay";
const QB_CHARGE_CARD_URI_SANDBOX =
  "https://devapi.cloud.nuskin.com/ffg-qb/v1/pay";
const GEO_IP_SERVICE_URI = "https://api.cloud.nuskin.com/geoip/v1/city";

/**
 * This donation form takes a persons name, email, distributor id (optional),
 * amount and credit card info and makes a charge using our Quickbooks URI to
 * the Force for Good Foundation.
 *
 * Amount options can be configured alongside a custom amount field. All text is
 * configurable for translations. Confirmation options include a url, a slotted
 * component, or a toast.
 */

export default {
  name: "NsDonationForm",
  components: {
    NsTextField,
    NsCreditCard,
    NsCreditCardDate,
    NsCurrencyTextField,
    NsAlert
    // NsSpinner
  },
  props: {
    /**
     * Will use production apis if true
     */
    isProduction: Boolean,
    /**
     * Month placeholder text
     */
    monthPlaceholderText: {
      type: String,
      default: "MM"
    },
    /**
     * Year placeholder text
     */
    yearPlaceholderText: {
      type: String,
      default: "YYYY"
    },
    /**
     * Invalid card nuber text
     */
    invalidCardNumberText: {
      type: String,
      default: "Card number is invalid"
    },
    /**
     * Field required text
     */
    fieldRequiredText: {
      type: String,
      default: "Field Required"
    },
    /**
     * Error label
     */
    errorLabel: {
      type: String,
      default: "Error"
    },
    /**
     * Success label
     */
    successLabel: {
      type: String,
      default: "Success"
    },
    /**
     * The site key for reCaptcha
     */
    captchaKey: {
      type: String,
      default: ""
    },
    /**
     * Submit label
     */
    submitLabel: {
      type: String,
      default: "Donate"
    },
    /**
     * Personal Info title
     */
    personalInfoTitle: {
      type: String,
      default: "Personal Information"
    },
    /**
     * Dist id label
     */
    nuskinIdLabel: {
      type: String,
      default: "Nu Skin ID"
    },
    /**
     * Optional label
     */
    optionalLabel: {
      type: String,
      default: "optional"
    },
    /**
     * Firstname label
     */
    firstNameLabel: {
      type: String,
      default: "First Name"
    },
    /**
     * Lastname label
     */
    lastNameLabel: {
      type: String,
      default: "Last Name"
    },
    /**
     * Amount title
     */
    amountTitle: {
      type: String,
      default: "How much would you like to donate?"
    },
    /**
     * Custom Amount label
     */
    amountLabel: {
      type: String,
      default: "Custom Amount"
    },
    /**
     * Button amounts ([{ value, displayText }])
     */
    amounts: {
      type: String,
      default: JSON.stringify([
        {
          value: 25,
          displayText: "$25"
        },
        {
          value: 50,
          displayText: "$50"
        }
      ])
    },
    /**
     * Show recurring option
     */
    showRecurringOption: Boolean,
    /**
     * Donation Type label
     */
    donationTypeLabel: {
      type: String,
      default: "Donation Type"
    },
    /**
     * One Time label
     */
    oneTimeLabel: {
      type: String,
      default: "One Time"
    },
    /**
     * Monthly label
     */
    monthlyLabel: {
      type: String,
      default: "Monthly"
    },
    /**
     * Monthly Terms message
     */
    monthlyTermsMessage: {
      type: String,
      default: ""
    },
    /**
     * Credit card title
     */
    cardTitle: {
      type: String,
      default: "Donate with Card"
    },
    /**
     * Email label
     */
    emailLabel: {
      type: String,
      default: "Email Address"
    },
    /**
     * Invalid email message
     */
    invalidEmailText: {
      type: String,
      default: "Please use a valid email"
    },
    /**
     * Card Number label
     */
    cardNumberLabel: {
      type: String,
      default: "Card Number"
    },
    /**
     * Expiration label
     */
    expiryLabel: {
      type: String,
      default: "Expiration Date"
    },
    /**
     * CVC label
     */
    cvcLabel: {
      type: String,
      default: "Security Code"
    },
    /**
     * Error message for a minimum amount
     */
    minDonationAmountText: {
      type: String,
      default: "Donation amount must be at least $1.00"
    },
    /**
     * Url that will redirect upon a successful donation reponse
     */
    thankYouUrl: {
      type: String,
      default: ""
    },
    /**
     * Processing text
     */
    processingText: {
      type: String,
      default: "Processing"
    },
    /**
     * Success message
     */
    successMessage: {
      type: String,
      default: "Your payment was successful!"
    },
    /**
     * Failed payment message
     */
    errorMessage: {
      type: String,
      default: "Something went wrong with your payment."
    }
  },
  data() {
    return {
      // form fields
      firstName: "",
      lastName: "",
      email: "",
      distId: "",
      buttonAmount: null,
      customAmount: "0.00",
      donationType: "oneTime",
      cardNumber: "",
      cardExpiry: { expMonth: "", expYear: "" },
      cardCvc: "",
      // error states
      errors: {},
      validDate: false,
      serverErrorMessage: "",
      // success states
      processing: false,
      showConfirmationSlot: false,
      paymentSuccessful: false,
      transaction: {},
      // availablePayments
      availablePayments
    };
  },
  computed: {
    donationAmount() {
      return +this.customAmount || +this.buttonAmount || "";
    },
    formattedDonationAmount() {
      return this.donationAmount
        ? ` $${this.formatMoney(this.donationAmount, 2)}`
        : "";
    },
    formIsValid() {
      const validEmail = this.email && emailPattern.test(this.email);
      return !!(
        this.cardNumber &&
        this.validDate &&
        this.cardCvc &&
        this.firstName &&
        this.lastName &&
        validEmail &&
        this.donationAmount >= 1
      );
    },
    /**
     * Causes donation buttons to break into separate lines at certain lengths
     * */
    donationBtnsStyle: function() {
      let numOfCol = this.buttonAmounts.length;
      if (this.buttonAmounts.length > 8) {
        numOfCol = 5;
      } else if (this.buttonAmounts.length > 5) {
        numOfCol = 4;
      }
      return { gridTemplateColumns: `repeat(${numOfCol}, 1fr)` };
    },
    buttonAmounts() {
      return fromJsonString(this.amounts, []);
    }
  },
  watch: {
    customAmount(value) {
      if (+value > 0) {
        this.buttonAmount = null;
      }
    }
  },
  mounted() {
    const el = document.getElementById("custom-amount");
    if (el) {
      el.setAttribute("autocomplete", "off");
    }
  },
  methods: {
    handleDismissAlert() {
      this.serverErrorMessage = "";
      if (this.paymentSuccessful) {
        this.clearForm();
      }
      this.paymentSuccessful = false;
    },
    backToDonationForm() {
      this.showConfirmationSlot = false;
      this.clearForm();
    },
    clearForm() {
      this.firstName = "";
      this.lastName = "";
      this.email = "";
      this.customAmount = "0.00";
      this.buttonAmount = null;
      this.distId = "";
      this.cardNumber = "";
      this.cardExpiry = {
        expMonth: "",
        expYear: ""
      };
      this.cardCvc = "";
    },
    checkValidation(prop) {
      let testFunction = null;

      if (prop === "email") {
        testFunction = this.validateEmail;
      } else if (prop === "donationAmount") {
        testFunction = value => +value >= 1;
      }

      this.errors = {
        ...this.errors,
        [prop]: testFunction ? !testFunction(this[prop]) : !this[prop]
      };
    },
    removeErrors(prop) {
      delete this.errors[prop];
    },
    validateEmail(value) {
      const pattern = /^[\s]*[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?[\s]*$/;
      return pattern.test(value);
    },
    focusCustomAmount(event) {
      event.target.select();
      this.removeErrors("donationAmount");
    },
    /**
     * Gets called when an amount is updated
     */
    updateAmount(amt) {
      this.buttonAmount = amt;
      this.customAmount = "0.00";
      this.removeErrors("donationAmount");
    },
    // formats to currency for the Donate button
    formatMoney(amount, decimalCount = 2, decimal = ".", thousands = ",") {
      const negativeSign = +amount < 0 ? "-" : "";
      const amountVal = Math.abs(Number(amount) || 0).toFixed(decimalCount);
      let i = parseInt(amountVal).toString();
      let j = i.length > 3 ? i.length % 3 : 0;

      return (
        negativeSign +
        (j ? i.substr(0, j) + thousands : "") +
        i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) +
        (decimalCount
          ? decimal +
            Math.abs(amount - i)
              .toFixed(decimalCount)
              .slice(2)
          : "")
      );
    },
    showErrors() {
      ["firstName", "lastName", "email", "donationAmount", "cardCvc"].forEach(
        prop => {
          this.checkValidation(prop);
        }
      );
      // show credit card element errors
      this.$refs.creditCardNumber.$vnode.elm.querySelector("input").focus();
      setTimeout(() => {
        this.$refs.creditCardNumber.$vnode.elm.querySelector("input").blur();
      }, 20);

      this.$refs.creditCardDate.blurringExpYear();
    },
    pay() {
      this.validDate = this.$refs.creditCardDate.isDateValid();
      this.handleDismissAlert();

      if (this.formIsValid) {
        this.processing = true;
        this.payWithCard();
      } else {
        this.showErrors();
      }
    },
    // paying with a card
    async payWithCard() {
      try {
        const captcha = await this.getCaptchaToken();
        const { data: tokenData } = await this.createQBToken();
        const token = tokenData.value;
        const { city, country, postalCode, state } = await this.getGeoIpInfo();
        const { data } = await this.chargeCardWithInfo({
          token,
          captcha,
          city,
          country,
          postalCode,
          state
        });
        this.transaction = {
          description: data.description,
          receiptId: data.receiptId,
          amount: parseFloat(data.amount).toFixed(2),
          transactionDate: data.txDate,
          cardLast4: data.last4
        };
        this.showChargeSuccess();
      } catch (error) {
        this.serverErrorMessage = (error || {}).message || this.errorMessage;
      } finally {
        this.processing = false;
      }
    },
    async chargeCardWithInfo({
      token,
      captcha,
      city,
      country,
      postalCode,
      state
    }) {
      const body = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        distId: this.distId,
        amount: this.donationAmount,
        currency: "USD",
        token,
        captcha,
        city,
        country,
        postalCode,
        state,
        recurring: this.showRecurringOption && this.donationType === "monthly"
      };
      return axios.post(
        this.isProduction ? QB_CHARGE_CARD_URI : QB_CHARGE_CARD_URI_SANDBOX,
        body
      );
    },
    async createQBToken() {
      const body = {
        card: {
          expYear: `20${this.cardExpiry.expYear}`,
          expMonth: `${this.cardExpiry.expMonth}`,
          name: `${this.firstName} ${this.lastName}`,
          cvc: this.cardCvc,
          number: this.cardNumber
        }
      };
      return axios.post(
        this.isProduction ? QB_TOKEN_URI : QB_TOKEN_URI_SANDBOX,
        body
      );
    },
    // call after a payment is successful to show success indication
    showChargeSuccess() {
      // clear amounts
      this.customAmount = "0.00";
      this.buttonAmount = null;

      if (this.thankYouUrl && this.thankYouUrl !== "#") {
        // go to thankyou page
        window.location.href = this.thankYouUrl;
      } else if (this.$scopedSlots.default) {
        // !thankYouUrl && using slot -> show slot component
        this.showConfirmationSlot = true;
      } else {
        // !thankYouUrl && !slot -> show toast
        this.paymentSuccessful = true;
      }
    },
    async getGeoIpInfo() {
      try {
        const { data } = await axios.get(GEO_IP_SERVICE_URI, {
          headers: {
            client_id: "3eee8207a8224d4bbc1c667438269591"
          }
        });
        return {
          city: data.city.names.en,
          country: data.country.iso_code,
          postalCode: data.postal.code,
          state: (data.subdivisions[0] || {}).iso_code
        };
      } catch (error) {
        return {
          city: "",
          country: "",
          postalCode: "",
          state: ""
        };
      }
    },
    async getCaptchaToken() {
      return new Promise((res, rej) => {
        window.grecaptcha.ready(async () => {
          try {
            const captcha = await window.grecaptcha.execute(this.captchaKey, {
              action: "submit"
            });
            res(captcha);
          } catch (error) {
            console.error(error);
            rej({ message: "Captcha failed" });
          }
        });
      });
    }
  }
};
</script>

<style lang="scss" module>
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";

.container {
  margin: 0 auto;
  padding: 30px 0px 60px;
  box-sizing: border-box;
}

.alertWrapper {
  display: flex;
  justify-content: center;
  margin: 40px 0;
  width: 100%;
}

.alert {
  padding: 0 20px;
  width: 100%;
}

.form {
  position: relative;
  display: flex;
  flex-direction: column;
}

.formSection {
  flex: 1 0 0;
  margin-bottom: 15px;
  padding: 0 20px;
}

.separator {
  display: none;
  margin: 39px 0 100px;
  border: 1px solid rgb(206, 208, 208);
  width: 2px;
  min-height: 331px;
  height: auto;
}

.sectionSpacer {
  height: 13px;
}

.donationTypes {
  margin-bottom: 20px;
}

.typeLabel {
  margin: 8px;

  &:first-child {
    margin-left: 0;
  }
}

.donationText {
  margin: 10px 0;
  font-size: 13px;
}

.label {
  display: block;
  font-size: 16px;
  line-height: 22px;
  margin-bottom: 4px;

  &.error-label {
    color: var(--color-error);
  }
}

.sectionTitle {
  margin-bottom: 15px;
  color: rgb(39, 37, 37);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.donationAmounts {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  margin: 0 -6px;
}

button.donationAmount {
  margin: 0 6px 15px;
  padding: 13px 4px;
  &:active {
    padding: 12px 3px;
    border-width: 2px;
  }
}

.expiryCvcRow {
  display: flex;
  margin-bottom: 25px;
}

.rowItem {
  flex: 1 0 0;
  margin-right: 15px;
  &:last-child {
    margin-right: 0;
  }
}

.buttonProcessing {
  pointer-events: none;
}

.processing span {
  animation-name: blink;
  animation-duration: 1.4s;
  animation-iteration-count: infinite;
  animation-fill-mode: both;
}

.processing span:nth-child(2) {
  animation-delay: 0.2s;
}

.processing span:nth-child(3) {
  animation-delay: 0.4s;
}

@media only screen and (min-width: 700px) {
  .container {
    padding: 50px 15px 60px;
  }

  .form {
    flex-direction: row;
    max-width: 782px;
  }

  .separator {
    display: block;
  }
}

@keyframes blink {
  0% {
    opacity: 0.2;
  }

  20% {
    opacity: 1;
  }

  100% {
    opacity: 0.2;
  }
}
</style>
<style lang="scss" scoped>
.text-field {
  margin-bottom: 20px;

  ::v-deep input[type="text"] {
    border: 1px solid #63666a;
  }

  &.error {
    ::v-deep div,
    ::v-deep label {
      color: #b2140a;
    }

    ::v-deep input[type="text"] {
      border: 1px solid #b2140a;
    }
  }
}
</style>
